/**
 * `fetch` 래퍼. `credentials: 'include'` 를 강제하고 `/api` prefix 를 자동 부착한다.
 *
 * 경로는 다음 규칙을 따른다:
 *   - `/api/...`    : 그대로 사용
 *   - `/auth/...`   : 그대로 (auth 라우트는 /api 하위가 아님)
 *   - `foo`, `/foo` : `/api/foo` 로 변환
 *   - `http(s)://`  : 외부 URL, 그대로 (credentials include 유지)
 *
 * Phase 0 에서는 최소 기능만. 에러 모델, 토큰 헤더, 재시도 등은 Phase 1+ 에서 추가.
 */

export interface HttpError extends Error {
  status: number;
  statusText: string;
  body?: unknown;
}

export function isHttpError(error: unknown): error is HttpError {
  return Boolean(
    error
      && typeof error === 'object'
      && 'status' in error
      && typeof (error as { status?: unknown }).status === 'number',
  );
}

export function getHttpErrorDetail(error: unknown): string | null {
  if (!isHttpError(error)) return null;
  const body = error.body;
  if (!body || typeof body !== 'object' || !('detail' in body)) return null;
  const detail = (body as { detail?: unknown }).detail;
  return typeof detail === 'string' && detail.trim() ? detail : null;
}

function resolveUrl(path: string): string {
  if (/^https?:\/\//i.test(path)) return path;
  if (path.startsWith('/api/') || path.startsWith('/auth/')) return path;
  const clean = path.startsWith('/') ? path.slice(1) : path;
  return `/api/${clean}`;
}

async function parseBody(res: Response): Promise<unknown> {
  const ct = res.headers.get('content-type') ?? '';
  if (ct.includes('application/json')) {
    try {
      return await res.json();
    } catch {
      return null;
    }
  }
  try {
    return await res.text();
  } catch {
    return null;
  }
}

export async function http<T = unknown>(
  path: string,
  init: RequestInit = {},
): Promise<T> {
  const url = resolveUrl(path);
  const res = await fetch(url, {
    credentials: 'include',
    ...init,
    headers: {
      Accept: 'application/json',
      ...(init.headers ?? {}),
    },
  });

  if (!res.ok) {
    const body = await parseBody(res);
    const err = new Error(
      `HTTP ${res.status} ${res.statusText} — ${url}`,
    ) as HttpError;
    err.status = res.status;
    err.statusText = res.statusText;
    err.body = body;
    throw err;
  }

  // 204 No Content 처리
  if (res.status === 204) {
    return undefined as T;
  }

  return (await parseBody(res)) as T;
}

export const httpJson = {
  get: <T = unknown>(path: string, init?: RequestInit) =>
    http<T>(path, { ...init, method: 'GET' }),

  post: <T = unknown>(path: string, body?: unknown, init?: RequestInit) =>
    http<T>(path, {
      ...init,
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(init?.headers ?? {}),
      },
      body: body === undefined ? undefined : JSON.stringify(body),
    }),

  put: <T = unknown>(path: string, body?: unknown, init?: RequestInit) =>
    http<T>(path, {
      ...init,
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        ...(init?.headers ?? {}),
      },
      body: body === undefined ? undefined : JSON.stringify(body),
    }),

  patch: <T = unknown>(path: string, body?: unknown, init?: RequestInit) =>
    http<T>(path, {
      ...init,
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
        ...(init?.headers ?? {}),
      },
      body: body === undefined ? undefined : JSON.stringify(body),
    }),

  delete: <T = unknown>(path: string, init?: RequestInit) =>
    http<T>(path, { ...init, method: 'DELETE' }),
};
